/* ==========================================================================
   Simply Creator — theme.js
   Vanilla JS, no dependencies. Every block below is independent, so removing
   one never breaks the others.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  root.className = root.className.replace('no-js', 'js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function on(el, evt, fn) { if (el) el.addEventListener(evt, fn); }

  function money(cents) {
    var format = window.SimplyCreator && window.SimplyCreator.moneyFormat;
    if (!format) return (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  var toastEl = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sc-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(function () { toastEl.classList.add('is-visible'); });
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 3200);
  }

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */

  function initReveal(scope) {
    var items = $$('.sc-reveal:not(.sc-in)', scope);
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('sc-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sc-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     Sticky header shadow
     ------------------------------------------------------------------ */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var measure = function () {
      root.style.setProperty('--sc-header-height', header.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', measure);

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      });
    }, { passive: true });
  }

  /* ------------------------------------------------------------------
     Drawers (menu, search, cart) — one shared controller
     ------------------------------------------------------------------ */

  var openDrawer = null;
  var lastFocused = null;

  function closeDrawer() {
    if (!openDrawer) return;
    openDrawer.classList.remove('is-open');
    openDrawer.setAttribute('aria-hidden', 'true');
    $$('[data-overlay]').forEach(function (o) { o.classList.remove('is-open'); });
    document.body.classList.remove('sc-no-scroll');
    $$('[aria-controls="' + openDrawer.id + '"]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    openDrawer = null;
    if (lastFocused) { lastFocused.focus(); lastFocused = null; }
  }

  function showDrawer(drawer, trigger) {
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) closeDrawer();
    lastFocused = trigger || document.activeElement;
    openDrawer = drawer;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    $$('[data-overlay]').forEach(function (o) { o.classList.add('is-open'); });
    document.body.classList.add('sc-no-scroll');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var focusable = drawer.querySelector('input:not([type="hidden"]), button, a[href]');
    if (focusable) setTimeout(function () { focusable.focus(); }, 220);
  }

  function initDrawers() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        showDrawer(document.getElementById(opener.getAttribute('data-drawer-open')), opener);
        return;
      }
      if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-overlay]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !openDrawer) return;
      closeDrawer();
    });

    /* Focus trap */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !openDrawer) return;
      var focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', openDrawer)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ------------------------------------------------------------------
     Announcement bar rotation
     ------------------------------------------------------------------ */

  function initAnnouncement() {
    $$('[data-announcement]').forEach(function (bar) {
      var slides = $$('.sc-announcement__slide', bar);
      if (slides.length < 2) return;
      var delay = parseInt(bar.getAttribute('data-interval'), 10) || 6000;
      var i = 0;
      setInterval(function () {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, delay);
    });
  }

  /* ------------------------------------------------------------------
     Product gallery
     ------------------------------------------------------------------ */

  function initGallery(scope) {
    $$('[data-gallery]', scope).forEach(function (gallery) {
      if (gallery._bound) return;
      gallery._bound = true;

      var main = $('[data-gallery-main]', gallery);
      var thumbs = $$('[data-gallery-thumb]', gallery);

      thumbs.forEach(function (thumb) {
        on(thumb, 'click', function () {
          var src = thumb.getAttribute('data-src');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          var img = main.querySelector('img');
          if (!img) return;
          img.src = src;
          if (srcset) img.srcset = srcset;
          img.alt = alt;
          thumbs.forEach(function (t) { t.setAttribute('aria-current', 'false'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });

      gallery._selectByMedia = function (mediaId) {
        var match = thumbs.filter(function (t) { return t.getAttribute('data-media-id') === String(mediaId); })[0];
        if (match) match.click();
      };
    });
  }

  /* ------------------------------------------------------------------
     Variant selection
     ------------------------------------------------------------------ */

  function initVariants(scope) {
    $$('[data-product-form]', scope).forEach(function (form) {
      if (form._bound) return;
      form._bound = true;

      var dataEl = $('[data-variant-json]', form.closest('[data-product-root]') || document);
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = $('[data-variant-id]', form);
      var priceEl = $('[data-variant-price]', form.closest('[data-product-root]'));
      var submit = $('[data-add-to-cart]', form);
      var submitText = submit ? $('[data-add-to-cart-text]', submit) : null;
      var gallery = $('[data-gallery]', form.closest('[data-product-root]') || document);
      var strings = (window.SimplyCreator && window.SimplyCreator.strings) || {};

      function currentOptions() {
        return $$('[data-option-index]', form)
          .filter(function (el) { return el.type !== 'radio' || el.checked; })
          .map(function (el) { return el.value; });
      }

      function update() {
        var selected = currentOptions();
        var match = variants.filter(function (v) {
          return selected.every(function (val, i) { return v.options[i] === val; });
        })[0];

        if (!match) {
          if (submit) { submit.setAttribute('disabled', 'disabled'); }
          if (submitText) submitText.textContent = strings.unavailable || 'Unavailable';
          return;
        }

        if (idInput) idInput.value = match.id;

        if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;

        if (submit) {
          if (match.available) {
            submit.removeAttribute('disabled');
            if (submitText) submitText.textContent = submit.getAttribute('data-default-text') || strings.addToCart || 'Add to cart';
          } else {
            submit.setAttribute('disabled', 'disabled');
            if (submitText) submitText.textContent = strings.soldOut || 'Sold out';
          }
        }

        if (gallery && gallery._selectByMedia && match.featured_media_id) {
          gallery._selectByMedia(match.featured_media_id);
        }

        if (history.replaceState && match.id) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url.toString());
        }
      }

      $$('[data-option-index]', form).forEach(function (el) { on(el, 'change', update); });
      update();
    });
  }

  /* ------------------------------------------------------------------
     Quantity steppers
     ------------------------------------------------------------------ */

  function initQuantity(scope) {
    $$('[data-qty]', scope).forEach(function (wrap) {
      if (wrap._bound) return;
      wrap._bound = true;
      var input = $('input', wrap);
      $$('[data-qty-step]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var step = parseInt(btn.getAttribute('data-qty-step'), 10);
          var min = parseInt(input.getAttribute('min'), 10) || 0;
          var next = (parseInt(input.value, 10) || 0) + step;
          input.value = Math.max(min, next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Cart — add, update, drawer refresh
     ------------------------------------------------------------------ */

  var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function refreshCartUI() {
    return fetch(rootUrl + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer-content]');
        var target = $('[data-cart-drawer-content]');
        if (fresh && target) {
          target.innerHTML = fresh.innerHTML;
          initQuantity(target);
          initReveal(target);
        }
        return fetch(rootUrl + 'cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        return cart;
      });
  }

  function initCart() {
    /* Quick add + product form AJAX */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-ajax-cart]');
      if (!form) return;
      if (!(window.SimplyCreator && window.SimplyCreator.cartDrawer)) return;

      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.classList.add('sc-loading');

      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
        .then(function (res) {
          if (!res.ok) {
            toast(res.body.description || res.body.message || 'Something went wrong.');
            return;
          }
          return refreshCartUI().then(function () {
            showDrawer(document.getElementById('sc-cart-drawer'), btn);
          });
        })
        .catch(function () { toast('Something went wrong. Please try again.'); })
        .finally(function () { if (btn) btn.classList.remove('sc-loading'); });
    });

    /* Line item quantity + remove, on the cart page and in the drawer */
    document.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      changeLine(remove.getAttribute('data-line'), 0, remove);
    });

    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      changeLine(input.getAttribute('data-line'), parseInt(input.value, 10) || 0, input);
    });
  }

  function changeLine(line, quantity, originEl) {
    if (!line) return;
    var container = originEl && originEl.closest('[data-cart-drawer-content], [data-cart-root]');
    if (container) container.classList.add('sc-loading');

    fetch(rootUrl + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        if ($('[data-cart-root]')) { window.location.reload(); return; }
        return refreshCartUI();
      })
      .catch(function () { toast('Could not update your cart.'); })
      .finally(function () { if (container) container.classList.remove('sc-loading'); });
  }

  /* ------------------------------------------------------------------
     Accordions — keep one open at a time inside a group
     ------------------------------------------------------------------ */

  function initAccordions(scope) {
    $$('[data-accordion-exclusive]', scope).forEach(function (group) {
      if (group._bound) return;
      group._bound = true;
      var items = $$('details', group);
      items.forEach(function (item) {
        on(item, 'toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) { if (other !== item) other.open = false; });
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Collection filters — auto-submit without a full reload
     ------------------------------------------------------------------ */

  function initFilters() {
    var form = $('[data-filter-form]');
    if (!form) return;

    function apply() {
      var params = new URLSearchParams(new FormData(form)).toString();
      var url = window.location.pathname + (params ? '?' + params : '');
      var results = $('[data-collection-results]');
      if (results) results.classList.add('sc-loading');

      fetch(url + (params ? '&' : '?') + 'section_id=main-collection')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-collection-results]');
          var freshAside = parsed.querySelector('[data-filter-form]');
          if (fresh && results) results.innerHTML = fresh.innerHTML;
          if (freshAside) form.innerHTML = freshAside.innerHTML;
          history.pushState({}, '', url);
          initReveal(results);
          initQuantity(results);
          if (results) results.classList.remove('sc-loading');
        })
        .catch(function () { window.location.href = url; });
    }

    form.addEventListener('change', function () { apply(); });
    form.addEventListener('submit', function (e) { e.preventDefault(); apply(); });

    var sort = $('[data-sort-select]');
    if (sort) {
      on(sort, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        window.location.href = url.toString();
      });
    }

    window.addEventListener('popstate', function () { window.location.reload(); });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init(scope) {
    initReveal(scope);
    initGallery(scope);
    initVariants(scope);
    initQuantity(scope);
    initAccordions(scope);
  }

  function boot() {
    initHeader();
    initDrawers();
    initAnnouncement();
    initCart();
    initFilters();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-run inside the Shopify theme editor when a section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    initHeader();
    initAnnouncement();
    initFilters();
  });
  document.addEventListener('shopify:section:select', function (e) { initReveal(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var details = e.target.closest('details');
    if (details) details.open = true;
  });

  window.SimplyCreatorTheme = { toast: toast, refreshCartUI: refreshCartUI, showDrawer: showDrawer, closeDrawer: closeDrawer, money: money };
})();
